/* eslint-disable @typescript-eslint/no-explicit-any */
import { defaultFieldRegistry } from "./fields/mappings"
import {
  FieldDefinition,
  FieldUniqueIdentifier,
  IFieldRegistry,
  resolvePath,
  ServiceName,
  SubmissionLayout,
} from "./utils/types"

/**
 * Map collection names to database table names
 * Only handles special cases where collection name differs from table name
 */
const COLLECTION_TO_TABLE_MAP: Record<string, string> = {
  "phyto-botanical-name": "phyto_botanical_name",
  "countries": "country"
}

/**
 * Extract table name from searchRepresentation collectionName
 * Example: "public:port" → "port", "public:countries" → "country"
 */
export const getTableNameFromSearchRepresentation = (
  fuid: FieldUniqueIdentifier,
): string | null => {
  const fieldDef = defaultFieldRegistry.get(fuid)
  if (!fieldDef?.searchRepresentation) return null

  const layouts = Object.values(fieldDef.searchRepresentation)
  if (layouts.length === 0) return null

  const collectionName = layouts[0].collectionName
  const collectionKey = collectionName.split(":")[1] || null

  if (!collectionKey) return null

  // Map collection name to actual DB table name (handles special cases)
  return COLLECTION_TO_TABLE_MAP[collectionKey] || collectionKey
}

/**
 * Check if a service path is a lookup field by searching in field-anchor registry
 * Supports both formats: "products[*].field" and "products.field"
 */
export const isLookupFieldByPath = (servicePath: string): boolean => {
  const normalizedPath = servicePath.replaceAll("[*]", "")

  for (const fieldDef of defaultFieldRegistry.values()) {
    for (const rep of Object.values(fieldDef.serviceRepresentations)) {
      if ("path" in rep) {
        if (rep.path.replaceAll("[*]", "") === normalizedPath) return true
      } else {
        for (const layoutRep of Object.values(rep)) {
          if (layoutRep?.path?.replaceAll("[*]", "") === normalizedPath)
            return true
        }
      }
    }
  }

  return false
}

/**
 * Get FUID for classification field using classificationCode
 * Returns null if classification FUID doesn't exist in registry
 */
export const getClassificationFuid = (
  classificationCode: string,
): FieldUniqueIdentifier | null => {
  const fuid = `_prod_cls_${classificationCode}` as FieldUniqueIdentifier
  return defaultFieldRegistry.get(fuid) ? fuid : null
}

// Helper functions for core lookup field conversions
// Determine submission layout from service names

const getSubmissionLayout = (
  fromService: string,
  targetService: string,
): SubmissionLayout | null => {
  const services = [fromService, targetService]
  if (services.some((s) => s.includes("PHYTO"))) return "PHYTO_EXPORT"
  if (services.some((s) => s.includes("TRANSFER"))) return "AP_TRANSFER"
  if (services.some((s) => s.includes("EXPORT"))) return "AP_EXPORT"
  return null
}
// Extract string value from various input formats (string, array of strings), otherwise null
const getStringValue = (val: any): string | null => {
  if (typeof val === "string") return val
  else if (Array.isArray(val) && val.length > 0 && typeof val[0] === "string")
    return val[0]
  else return null
}
// Extract name property from GraphQL objects or nested array structures
const getObjectName = (val: any): string | null => {
  // Direct object with name property
  if (val?.name && typeof val.name === "string") return val.name
  // Handle nested arrays: drill down to find name property
  if (Array.isArray(val) && val.length > 0) {
    const nested = Array.isArray(val[0]) ? val[0][0] : val[0]
    // Only return name if object doesn't have id (avoid search objects)
    if (nested?.name && !nested.id) return nested.name
  }
  return null
}
// Transform value based on coreRepresentation config
export const transformValue = (
  value: any,
  fieldDef: FieldDefinition,
  isToGraphQL: boolean,
  fromService: string,
  targetService: string,
): any => {
  // Determine which submission layout applies to this service pair
  const submissionLayout = getSubmissionLayout(fromService, targetService)
  // Skip transformation if no layout found or field has no core representation config
  if (!submissionLayout || !fieldDef.coreRepresentation?.[submissionLayout])
    return value

  // Handle arrays by transforming each element
  if (Array.isArray(value)) {
    return value.map((item) =>
      transformValue(item, fieldDef, isToGraphQL, fromService, targetService),
    )
  }

  if (isToGraphQL) {
    // Converting TO GraphQL format: string → {id: null, name: string} object
    const stringVal = getStringValue(value)
    return stringVal ? { id: null, name: stringVal } : value
  } else {
    // Converting FROM GraphQL format: {id, name} object → string name
    if (isCoreLookupObject(value)) return value // Already in correct format
    const nameVal = getObjectName(value)
    return nameVal || value // Extract name property or return original
  }
}

// Check if value is a core lookup object (id and name can be null)
export const isCoreLookupObject = (
  val: any,
): val is { id: string | null; name: string | null } => {
  return (
    val !== null &&
    typeof val === "object" &&
    !Array.isArray(val) &&
    "id" in val &&
    "name" in val
  )
}
/**
 * Get service path for a given service name and FUID string
 * Returns null if FUID doesn't exist or service representation not found
 *
 * @example
 * getServicePathByFuid('DFE', '_req_loadingPort') // returns 'loadingPort'
 * getServicePathByFuid('CORE_TRANSFER_INPUT', '_prod_container_id') // returns transfer-specific path
 * getServicePathByFuid('B2G_SUBMISSION_PHYTO_ERROR_FIELD', '_seq_number') // returns 'Identifiers.CertificateID'
 */
export const getServicePathByFuid = (
  serviceName: string,
  fuid: string,
  registry: IFieldRegistry = defaultFieldRegistry,
): string | null => {
  const fieldDef = registry.get(fuid as FieldUniqueIdentifier)
  if (!fieldDef) return null

  const serviceRep = fieldDef.serviceRepresentations[serviceName as ServiceName]
  return resolvePath(serviceRep) ?? null
}

/**
 * Get complete field definition for a given FUID string
 * Returns null if FUID doesn't exist in registry
 *
 * @example
 * getFieldDefinitionByFuid('_req_loadingPort')
 * // returns { fuid, serviceRepresentations, searchRepresentation, ... }
 */
export const getFieldDefinitionByFuid = (
  fuid: string,
  registry: IFieldRegistry = defaultFieldRegistry,
): FieldDefinition | null => {
  return registry.get(fuid as FieldUniqueIdentifier) ?? null
}
